'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { FileText, ImageUp, Scan, Trash2, X } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

import { cn } from '@/lib/utils';
import { API_ENDPOINTS } from '@/lib/api/config';
import { UploadProgress } from '@/components/upload-progress';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { isProblemPage, type BatchPageRecord } from '../types';
import { PHOTO_SCANNING_HASH } from '../constants';
import { FilterChip } from './filter-chip';
import { PageMatchLabel, PageMatchOverlay } from './page-status-indicator';

// ─── Slots ─────────────────────────────────────────────────────────────────
// A PDF-viewer-style rail shows every page of the document, scanned or not.
// We model each slot 1..N so un-scanned slots render as a blank page (with its
// number) exactly like a real PDF viewer, while scanned slots render the actual
// page thumbnail. In `problems` filter mode only real problem pages are shown.
type RailSlot =
  | { kind: 'page'; page: BatchPageRecord; seq: number }
  | { kind: 'blank'; seq: number };

interface PageThumbnailRailProps {
  batchId: number;
  actualPages: BatchPageRecord[];
  total: number;
  // The page currently shown in the viewer — highlighted here and scrolled
  // into view when selection changes from the viewer.
  activePageId: number | null;
  onSelectPage: (pageId: number) => void;
  onClose: () => void;
  // Filter (All / Problems) — mirrors what the QR grid used to expose.
  gridFilter: 'all' | 'problems';
  onSetGridFilter: (filter: 'all' | 'problems') => void;
  // Per-page actions. Hidden when the batch is read-only (handed off to QA).
  readOnly?: boolean;
  onDeletePage: (pageId: number) => void;
  onRescanPage: (pageId: number, file: File) => void | Promise<void>;
  // Hardware re-scan of an existing page — only surfaced when the scanner is
  // the active capture mode (configurations toggle). Re-acquires from the DWT
  // scanner and replaces the page's image.
  scannerEnabled?: boolean;
  onHardwareRescan?: (pageId: number) => void;
  isDeletePending?: boolean;
  isRescanPending?: boolean;
  // Byte-level progress for an in-flight per-page rescan (owned by the parent).
  uploadProgress?: number | null;
}

// ─── Single thumbnail card ───────────────────────────────────────────────────
function RailThumb({
  slot,
  batchId,
  isActive,
  readOnly,
  scannerEnabled,
  isDeletePending,
  isRescanPending,
  onSelect,
  onRequestDelete,
  onRescan,
  onHardwareRescan
}: {
  slot: RailSlot;
  batchId: number;
  isActive: boolean;
  readOnly: boolean;
  scannerEnabled: boolean;
  isDeletePending: boolean;
  isRescanPending: boolean;
  onSelect: (pageId: number) => void;
  onRequestDelete: (pageId: number) => void;
  onRescan: (pageId: number, file: File) => void | Promise<void>;
  onHardwareRescan?: (pageId: number) => void;
}) {
  const t = useTranslations('photoScanning');
  const page = slot.kind === 'page' ? slot.page : null;
  const rescanInputRef = useRef<HTMLInputElement>(null);

  // Mirror the grid's real-image test: a `scanned://` placeholder ref means the
  // row exists but has no stored bytes yet, so it should read as a blank page.
  const hasRealImage = Boolean(
    page?.file_ref && !page.file_ref.startsWith('scanned://')
  );
  const thumbUrl = hasRealImage
    ? API_ENDPOINTS.PHOTO_SCANNING.PAGE_THUMBNAIL(batchId, page!.id)
    : null;
  // Same URL the viewer requests → the shared LRU blob cache dedupes the fetch.
  const blobUrl = useAuthenticatedBlobUrl(thumbUrl);

  const label = `P-${slot.seq}`;
  const clickable = Boolean(page);
  const isMismatch = page?.qr_status === 'mismatched';
  const isUnverified = page?.qr_status === 'unverified';
  const isProblem = page ? isProblemPage(page) : false;
  const showActions = Boolean(page) && !readOnly;

  return (
    <div className='flex flex-col items-center gap-1 px-2 pb-3'>
      <div
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={() => page && onSelect(page.id)}
        onKeyDown={(e) => {
          if (page && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect(page.id);
          }
        }}
        aria-current={isActive ? 'true' : undefined}
        aria-label={t('pageLabel', { n: label })}
        className={cn(
          'group relative flex aspect-[3/4] w-full items-center justify-center overflow-hidden rounded-md border bg-white shadow-sm transition-all dark:bg-slate-50',
          clickable
            ? 'cursor-pointer hover:shadow-md'
            : 'cursor-default border-slate-200 dark:border-slate-300',
          isActive
            ? 'border-violet-500 ring-2 ring-violet-400 ring-offset-1 dark:ring-offset-slate-900'
            : isMismatch
              ? 'border-rose-400 ring-1 ring-rose-200 dark:border-rose-600 dark:ring-rose-900/40'
              : isUnverified
                ? 'border-amber-400 ring-1 ring-amber-200 dark:border-amber-600 dark:ring-amber-900/40'
                : clickable
                  ? 'border-slate-200 hover:border-violet-300 dark:border-slate-300'
                  : undefined
        )}
      >
        {hasRealImage ? (
          blobUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- authenticated thumbnail; next/image proxy cannot forward Bearer token
            <img
              src={blobUrl}
              alt={t('pageLabel', { n: label })}
              loading='lazy'
              decoding='async'
              className={cn(
                'h-full w-full object-contain',
                isProblem && 'opacity-70 saturate-50'
              )}
            />
          ) : (
            <div
              className='h-full w-full animate-pulse bg-slate-200 dark:bg-slate-300'
              aria-hidden
            />
          )
        ) : (
          // Blank page placeholder for a slot that has not been scanned yet.
          <FileText
            className='h-8 w-8 text-slate-300 dark:text-slate-400'
            aria-hidden
          />
        )}

        {/* Success-match overlay — full-cover tint + centered icon over the
            thumbnail (qr-review style). Non-interactive so clicks select the
            page; covered by the action overlay on hover. */}
        {page && <PageMatchOverlay page={page} />}

        {/* Action toolbar overlaid on the thumbnail. Always visible for problem
            pages (operator's primary CTA); fades in on hover for healthy pages.
            Hidden once the batch is read-only (handed off to QA). */}
        {showActions && (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center gap-1.5 bg-black/55 backdrop-blur-[1px] transition-opacity',
              isProblem
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
            )}
          >
            {scannerEnabled && onHardwareRescan && (
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  onHardwareRescan(page!.id);
                }}
                className='inline-flex h-7 w-7 items-center justify-center rounded bg-white/90 text-violet-700 shadow hover:bg-white dark:bg-slate-900/80 dark:text-violet-200 dark:hover:bg-slate-900'
                title={t('rescanWithScanner')}
                aria-label={t('rescanWithScanner')}
              >
                <Scan className='h-4 w-4' />
              </button>
            )}
            {/* Manual-upload replace — only in upload mode. In scanner mode the
                hardware re-scan above is the re-capture path, so this is hidden
                to keep the two capture modes mutually exclusive. */}
            {!scannerEnabled && (
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  rescanInputRef.current?.click();
                }}
                disabled={isRescanPending}
                className='inline-flex h-7 w-7 items-center justify-center rounded bg-white/90 text-violet-700 shadow hover:bg-white disabled:opacity-40 dark:bg-slate-900/80 dark:text-violet-200 dark:hover:bg-slate-900'
                title={t('replaceImage')}
                aria-label={t('replaceImage')}
              >
                <ImageUp className='h-4 w-4' />
              </button>
            )}
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete(page!.id);
              }}
              disabled={isDeletePending}
              className='inline-flex h-7 w-7 items-center justify-center rounded bg-white/90 text-rose-600 shadow hover:bg-white disabled:opacity-40 dark:bg-slate-900/80 dark:text-rose-300 dark:hover:bg-slate-900'
              title={t('deletePage')}
              aria-label={t('deletePage')}
            >
              <Trash2 className='h-4 w-4' />
            </button>
          </div>
        )}
      </div>
      <span
        className={cn(
          'font-mono text-[11px] font-semibold',
          isActive
            ? 'text-violet-600 dark:text-violet-300'
            : isMismatch
              ? 'text-rose-600 dark:text-rose-300'
              : isUnverified
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-slate-500 dark:text-slate-400'
        )}
      >
        {label}
      </span>
      {/* Match / no-match text — says whether the page matched, qr-review style. */}
      {page && <PageMatchLabel page={page} />}

      {page && !readOnly && (
        <input
          ref={rescanInputRef}
          type='file'
          accept='image/*'
          className='hidden'
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void onRescan(page.id, file);
          }}
        />
      )}
    </div>
  );
}

// ─── Rail ────────────────────────────────────────────────────────────────────
/**
 * PDF-viewer-style page thumbnail rail — the single page list on the scanning
 * page. Clicking a thumbnail drives the main viewer. It also carries the
 * features that used to live in the separate QR grid: All/Problems filters, a
 * per-page success-match status overlay, and per-page delete / replace-image /
 * hardware re-scan actions. Virtualized so a large container only ever mounts
 * (and fetches) the visible thumbnails.
 */
export function PageThumbnailRail({
  batchId,
  actualPages,
  total,
  activePageId,
  onSelectPage,
  onClose,
  gridFilter,
  onSetGridFilter,
  readOnly = false,
  onDeletePage,
  onRescanPage,
  scannerEnabled = false,
  onHardwareRescan,
  isDeletePending = false,
  isRescanPending = false,
  uploadProgress
}: PageThumbnailRailProps) {
  const t = useTranslations('photoScanning');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Delete confirmation lives in the URL hash (#delete-page/<pageId>) so it is
  // deep-linkable and closes on browser Back. Destructure the stable callbacks.
  const {
    open: deletePageOpen,
    param: deletePageId,
    openWith: openDeletePage,
    onOpenChange: closeDeletePage
  } = useHashParamDialog(PHOTO_SCANNING_HASH.deletePage);

  const problemCount = useMemo(
    () => actualPages.filter(isProblemPage).length,
    [actualPages]
  );

  const slots = useMemo<RailSlot[]>(() => {
    // Problems mode: only the real problem pages, no blank placeholders.
    if (gridFilter === 'problems') {
      return actualPages
        .filter(isProblemPage)
        .map((page) => ({ kind: 'page' as const, page, seq: page.sequence }));
    }
    const pageBySeq = new Map<number, BatchPageRecord>();
    for (const p of actualPages) pageBySeq.set(p.sequence, p);
    const maxSeq = actualPages.reduce((m, p) => Math.max(m, p.sequence), 0);
    const limit = Math.max(total, maxSeq);
    const out: RailSlot[] = [];
    for (let seq = 1; seq <= limit; seq++) {
      const page = pageBySeq.get(seq);
      out.push(page ? { kind: 'page', page, seq } : { kind: 'blank', seq });
    }
    return out;
  }, [actualPages, total, gridFilter]);

  const virtualizer = useVirtualizer({
    count: slots.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 240,
    overscan: 3
  });

  // Keep the active thumbnail in view when selection changes from the viewer's
  // prev/next controls.
  const activeIndex = useMemo(
    () =>
      slots.findIndex((s) => s.kind === 'page' && s.page.id === activePageId),
    [slots, activePageId]
  );
  useEffect(() => {
    if (activeIndex >= 0) {
      virtualizer.scrollToIndex(activeIndex, { align: 'auto' });
    }
  }, [activeIndex, virtualizer]);

  return (
    <aside className='hidden min-h-0 flex-col border-e bg-slate-50/60 lg:flex dark:bg-slate-900/40'>
      <div className='flex shrink-0 items-center justify-between border-b px-3 py-2'>
        <span className='text-xs font-semibold text-slate-600 dark:text-slate-300'>
          {t('thumbnailRailTitle')}
        </span>
        <button
          type='button'
          onClick={onClose}
          title={t('closeThumbnails')}
          aria-label={t('closeThumbnails')}
          className='inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200'
        >
          <X className='h-3.5 w-3.5' aria-hidden />
        </button>
      </div>

      {/* Filters — mirror the QR grid's All / Problems chips. */}
      <div className='flex shrink-0 items-center gap-1 border-b px-3 py-2'>
        <FilterChip
          active={gridFilter === 'all'}
          count={actualPages.length}
          label={t('filterAll')}
          tone='slate'
          onClick={() => onSetGridFilter('all')}
        />
        <FilterChip
          active={gridFilter === 'problems'}
          count={problemCount}
          label={t('filterProblems')}
          tone='amber'
          onClick={() => onSetGridFilter('problems')}
        />
      </div>

      {/* Per-page rescan upload progress (bytes). Renders nothing while idle. */}
      <UploadProgress value={uploadProgress} className='px-3 py-1.5' />

      {slots.length === 0 ? (
        <div className='flex flex-1 items-center justify-center p-4 text-center text-xs text-slate-400 dark:text-slate-500'>
          {t('thumbnailRailEmpty')}
        </div>
      ) : (
        <div ref={scrollRef} className='min-h-0 flex-1 overflow-y-auto py-2'>
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative'
            }}
          >
            {virtualizer.getVirtualItems().map((vi) => {
              const slot = slots[vi.index];
              const isActive =
                slot.kind === 'page' && slot.page.id === activePageId;
              return (
                <div
                  key={vi.key}
                  data-index={vi.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    insetInlineStart: 0,
                    width: '100%',
                    transform: `translateY(${vi.start}px)`
                  }}
                >
                  <RailThumb
                    slot={slot}
                    batchId={batchId}
                    isActive={isActive}
                    readOnly={readOnly}
                    scannerEnabled={scannerEnabled}
                    isDeletePending={isDeletePending}
                    isRescanPending={isRescanPending}
                    onSelect={onSelectPage}
                    onRequestDelete={(id) => openDeletePage(String(id))}
                    onRescan={onRescanPage}
                    onHardwareRescan={onHardwareRescan}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AlertDialog
        open={deletePageOpen}
        onOpenChange={(o) => {
          if (!o) closeDeletePage(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deletePageConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deletePageConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className='bg-rose-600 hover:bg-rose-700'
              onClick={() => {
                const pid = deletePageId != null ? Number(deletePageId) : null;
                if (pid != null) {
                  onDeletePage(pid);
                  closeDeletePage(false);
                }
              }}
            >
              {t('deletePage')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
